import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { Copy, Sparkles, Eye, EyeOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/reset-password")({
  head: () => ({ meta: [{ title: "Wachtwoord herstellen" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: ResetPassword,
});

function generatePassword(length = 16): string {
  const charset = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%*?";
  const cryptoObj = typeof window !== "undefined" ? window.crypto : undefined;
  if (cryptoObj?.getRandomValues) {
    const arr = new Uint32Array(length);
    cryptoObj.getRandomValues(arr);
    return Array.from(arr, (n) => charset[n % charset.length]).join("");
  }
  let out = "";
  for (let i = 0; i < length; i++) out += charset[Math.floor(Math.random() * charset.length)];
  return out;
}

function ResetPassword() {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const handleGenerate = () => {
    const generated = generatePassword(16);
    setPw(generated);
    setPw2(generated);
    setShow(true);
    toast.success("Wachtwoord gegenereerd — vergeet niet te kopiëren");
  };

  const handleCopy = async () => {
    if (!pw) return toast.error("Geen wachtwoord om te kopiëren");
    try {
      await navigator.clipboard.writeText(pw);
      toast.success("Wachtwoord gekopieerd");
    } catch {
      toast.error("Kopiëren mislukt");
    }
  };

  const submit = async () => {
    if (!pw) return toast.error("Vul een wachtwoord in");
    if (pw !== pw2) return toast.error("Wachtwoorden komen niet overeen");
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password: pw });
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Wachtwoord bijgewerkt");
    nav({ to: "/account" });
  };

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-4">
      <Link to="/" className="mb-6 text-sm text-muted-foreground hover:underline">← Terug</Link>
      <h1 className="text-2xl font-bold mb-2">Nieuw wachtwoord instellen</h1>
      <p className="text-sm text-muted-foreground mb-4">
        Kies je eigen wachtwoord. Je bepaalt zelf hoe sterk het is — we stellen geen eisen.
      </p>
      <div className="space-y-3">
        <div className="space-y-1">
          <Label>Nieuw wachtwoord</Label>
          <div className="flex gap-2">
            <Input
              type={show ? "text" : "password"}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              autoComplete="new-password"
            />
            <Button type="button" variant="outline" size="icon" onClick={() => setShow((s) => !s)} aria-label={show ? "Verberg" : "Toon"}>
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
        </div>
        <div className="space-y-1">
          <Label>Bevestig</Label>
          <Input
            type={show ? "text" : "password"}
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            autoComplete="new-password"
          />
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={handleGenerate} className="flex-1">
            <Sparkles className="mr-2 h-4 w-4" /> Genereer wachtwoord
          </Button>
          <Button type="button" variant="outline" onClick={handleCopy} className="flex-1">
            <Copy className="mr-2 h-4 w-4" /> Kopieer wachtwoord
          </Button>
        </div>
        <Button onClick={submit} disabled={busy} className="w-full">Opslaan</Button>
      </div>
    </div>
  );
}
